import tokenService from '../services/tokenService';
import { safeConsole } from './productionUtils';

// Get the API base URL
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

/**
 * Make an authenticated API request
 * @param {string} endpoint - API endpoint path
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data or error
 */
export const makeAuthenticatedRequest = async (endpoint, options = {}) => {
  try {
    if (process.env.NODE_ENV === 'development' && Math.random() < 0.02) {
      safeConsole.log(' Making authenticated request to:', endpoint);
    }
    
    // Ensure we have a valid token before making the request
    const token = tokenService.getAccessToken();
    if (!token) {
      safeConsole.warn('🔐 No authentication token found');
      return { 
        success: false, 
        error: 'Your session has expired. Please refresh the page and log in again.' 
      };
    }
    
    safeConsole.log('🔐 Token found for request:', !!token);
    safeConsole.log('🔐 Token preview:', token ? `${token.substring(0, 20)}...` : 'null');
    safeConsole.log('🔍 API Utils Fix Version: 2.1 - Header merging fixed!');

    // Get authorization header
    let authHeader;
    try {
      authHeader = tokenService.getAuthHeader();
      safeConsole.log('🔐 Auth header created:', Object.keys(authHeader));
      safeConsole.log('🔐 Authorization value exists:', !!authHeader.Authorization);
      safeConsole.log('🔐 Authorization header preview:', authHeader.Authorization ? `${authHeader.Authorization.substring(0, 30)}...` : 'null');
    } catch (error) {
      safeConsole.error(' Failed to get auth header:', error.message);
      return { 
        success: false, 
        error: 'Your session has expired. Please refresh the page and log in again.' 
      };
    }
    
    // Construct the full URL
    const url = `${API_BASE_URL}${endpoint}`;
    
    // Default options
    const defaultOptions = {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
        ...(options.headers || {})
      }
    };
    
    // Merge with user-provided options - FIXED: Properly merge headers
    const finalOptions = { 
      ...defaultOptions, 
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
        ...(options.headers || {})
      }
    };
    
    safeConsole.log('🔍 Making request to:', url);
    safeConsole.log('🔍 Final headers:', finalOptions.headers);
    safeConsole.log('🔍 Request method:', finalOptions.method);
    
    // Special debugging for league settings requests
    if (endpoint.includes('league-settings')) {
      safeConsole.log('🔍 LEAGUE SETTINGS REQUEST DEBUG:');
      safeConsole.log('🔍 Authorization header present:', !!finalOptions.headers.Authorization);
      safeConsole.log('🔍 Authorization header value:', finalOptions.headers.Authorization ? 'Bearer ***' : 'MISSING');
      safeConsole.log('🔍 All headers:', Object.keys(finalOptions.headers));
    }
    
    const response = await fetch(url, finalOptions);
    
    // Check if token is expiring soon
    if (response.headers.get('X-Token-Expiring-Soon')) {
      // Token is about to expire, refresh it in the background
      tokenService.refreshToken().catch(err => 
        safeConsole.error('Background token refresh failed:', err)
      );
    }
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle 401 Unauthorized - attempt token refresh and retry
      if (response.status === 401 && errorData.error === 'Invalid token') {
        safeConsole.log(' Token invalid, attempting refresh and retry...');
        
        try {
          const refreshResult = await tokenService.refreshToken();
          if (refreshResult.success) {
            safeConsole.log(' Token refreshed successfully, retrying request...');
            
            // Update auth header with new token
            const newAuthHeader = tokenService.getAuthHeader();
            const retryOptions = {
              ...finalOptions,
              headers: {
                ...finalOptions.headers,
                ...newAuthHeader
              }
            };
            
            // Retry the original request with new token
            const retryResponse = await fetch(url, retryOptions);
            
            if (retryResponse.ok) {
              const retryData = await retryResponse.json();
              return { success: true, data: retryData };
            } else {
              // If retry also fails, fall through to normal error handling
              const retryErrorData = await retryResponse.json().catch(() => ({}));
              safeConsole.log(' Retry request also failed:', retryErrorData);
            }
          } else {
            safeConsole.log(' Token refresh failed:', refreshResult.error);
          }
        } catch (refreshError) {
          safeConsole.error(' Token refresh attempt failed:', refreshError);
        }
      }
      
      // Handle validation errors with multiple error messages
      let errorMessage;
      if (errorData.errors && Array.isArray(errorData.errors)) {
        // Extract the first error message from validation errors
        errorMessage = errorData.errors[0]?.message || 'Validation failed';
      } else if (errorData.error) {
        // Single error message
        errorMessage = errorData.error;
      } else {
        // Fallback to generic HTTP error
        errorMessage = `HTTP error! status: ${response.status}`;
      }
      
      return { 
        success: false, 
        error: errorMessage,
        errors: errorData.errors, // Include full errors array for detailed handling
        status: response.status
      };
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    if (process.env.NODE_ENV === 'development') {
      safeConsole.error(' Request error:', error);
    }
    return { 
      success: false, 
      error: error.message || 'Failed to make API request' 
    };
  }
};

/**
 * Make a request that authenticates when a session exists but still works
 * for logged-out visitors — for endpoints that serve public-league data
 * (e.g. the manual-draft "Record Picks" read-only link) without requiring
 * an account, while still resolving the caller's role when they are signed in.
 * @param {string} endpoint - API endpoint path
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data or error
 */
export const makeOptionalAuthRequest = async (endpoint, options = {}) => {
  try {
    const token = tokenService.getAccessToken();
    const authHeader = token ? { Authorization: `Bearer ${token}` } : {};

    const url = `${API_BASE_URL}${endpoint}`;
    const finalOptions = {
      method: 'GET',
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...authHeader,
        ...(options.headers || {})
      }
    };

    const response = await fetch(url, finalOptions);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      return {
        success: false,
        error: errorData.error || `HTTP error! status: ${response.status}`,
        status: response.status
      };
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return {
      success: false,
      error: error.message || 'Failed to make API request'
    };
  }
};

/**
 * Make a public API request (no authentication required)
 * @param {string} endpoint - API endpoint path
 * @param {Object} options - Fetch options
 * @returns {Promise<Object>} Response data or error
 */
export const makePublicRequest = async (endpoint, options = {}) => {
  try {
    // Construct the full URL
    const url = `${API_BASE_URL}${endpoint}`;
    
    const response = await fetch(url, {
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      ...options,
    });
    
    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      
      // Handle validation errors with multiple error messages
      let errorMessage;
      if (errorData.errors && Array.isArray(errorData.errors)) {
        // Extract the first error message from validation errors
        errorMessage = errorData.errors[0]?.message || 'Validation failed';
      } else if (errorData.error) {
        // Single error message
        errorMessage = errorData.error;
      } else {
        // Fallback to generic HTTP error
        errorMessage = `HTTP error! status: ${response.status}`;
      }
      
      return { 
        success: false, 
        error: errorMessage,
        errors: errorData.errors, // Include full errors array for detailed handling
        status: response.status
      };
    }

    const data = await response.json();
    return { success: true, data };
  } catch (error) {
    return { 
      success: false, 
      error: error.message || 'Failed to make API request' 
    };
  }
}; 